import { throwIfAborted } from '../abort.js';
import { OnrampApiError } from '../client.js';
import { bindOnrampCache } from './cache-lifecycle.js';
const cooldowns = new WeakMap();
const DEFAULT_RETRY_MS = 7000;
export function operationQuery(client, cache, operationId) {
    bindOnrampCache(client, cache);
    const queryKey = ['onramp', client.cacheKey, 'operation', operationId];
    return {
        queryKey,
        retry: false,
        staleTime: 0,
        meta: { persist: false },
        async queryFn({ signal }) {
            throwIfAborted(signal);
            const cooldown = cooldowns.get(client)?.get(operationId);
            if (cooldown && cooldown.until > Date.now())
                throw cooldown.error;
            try {
                const result = await client.getOperation(operationId, signal);
                if (cooldowns.get(client)?.get(operationId) === cooldown)
                    cooldowns.get(client)?.delete(operationId);
                const previous = cache.getQueryData(queryKey);
                return previous && previous.version > result.data.version ? previous : result.data;
            }
            catch (error) {
                if (!signal.aborted &&
                    error instanceof OnrampApiError &&
                    (error.status === 429 || error.retryAfterMs > 0)) {
                    const delay = Number.isFinite(error.retryAfterMs) && error.retryAfterMs > 0
                        ? Math.min(2147483647, error.retryAfterMs)
                        : DEFAULT_RETRY_MS;
                    let clientCooldowns = cooldowns.get(client);
                    if (clientCooldowns === undefined) {
                        clientCooldowns = new Map();
                        cooldowns.set(client, clientCooldowns);
                    }
                    clientCooldowns.set(operationId, {
                        until: Math.max(clientCooldowns.get(operationId)?.until ?? 0, Date.now() + delay),
                        error,
                    });
                }
                throw error;
            }
        },
    };
}
export async function refreshOperationQuery(client, cache, operationId, signal) {
    if (signal.aborted)
        throw new Error('onramp_refresh_aborted');
    const query = operationQuery(client, cache, operationId);
    const abort = () => {
        void cache.cancelQueries({ queryKey: query.queryKey, exact: true });
    };
    signal.addEventListener('abort', abort, { once: true });
    try {
        // Query observers must receive errors too, so a revoked receipt is no longer displayed.
        const snapshot = await cache.fetchQuery(query);
        if (signal.aborted)
            throw new Error('onramp_refresh_aborted');
        return snapshot;
    }
    finally {
        signal.removeEventListener('abort', abort);
    }
}
